import React, { useState, useEffect, useContext } from 'react';
import momentTZ from 'moment-timezone';
import { getSFTime } from 'services/formatDateToTimezone/formatDateToTimezone';
import PropTypes from 'prop-types';
import Spinner from 'components/shared/Spinner';
import MobileContext from 'context/MobileContext';
import { horizontalLogo } from 'assets/logos';
import { ClassesHeader } from './ClassesHeader';
import { ClassesContent } from './ClassesContent';
import { calculateFourWeeksDates } from './service';
import styles from './styles.module.css';
import { useLocation } from 'react-router-dom';

// Cutover date reached — new API enabled effective July 14, 2026 (America/Los_Angeles)
const useNewApi = momentTZ.tz('America/Los_Angeles').isAfter(
  momentTZ.tz('2026-07-14 00:00', 'America/Los_Angeles')
);

const GuestClasses = ({
  loadClubs,
  clubs,
  classes,
  openClasses,
  openCenters,
  isRequesting,
  loadClassesSchedule,
  loadOpenClasses,
  loadOpenCenters,
  loadClubsIsRequesting,
  changeSelectedClub,
}) => {
  const isMobile = useContext(MobileContext);
  const datesRange = calculateFourWeeksDates();
  const [targetDate, setTargetDate] = useState(datesRange[0]);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const locationQuery = queryParams.get('locationID');
  const [selectedClub, setSelectedClub] = useState( locationQuery ? locationQuery : 'all');

  // OLD API: load clubs + fetch on club change
  useEffect(() => {
    if (!useNewApi) {
      loadClubs();
      loadClassesSchedule({
        startDate: targetDate.date || getSFTime(),
        clubId: selectedClub,
      });
    }
    // eslint-disable-next-line
  }, [selectedClub]);

  // OLD API: re-fetch when selected date changes
  useEffect(() => {
    if (!useNewApi) {
      loadClassesSchedule({
        startDate: targetDate.date,
        clubId: selectedClub,
      });
    }
    // eslint-disable-next-line
  }, [targetDate]);

  // NEW API: fetch all centers and 15-day classes once on mount
  useEffect(() => {
    if (useNewApi) {
      loadOpenCenters();
      loadOpenClasses();
    }
    // eslint-disable-next-line
  }, []);

  const displayClubs = useNewApi
    ? openCenters.map(c => ({
        name: c.shortName,
        clubAutomationClubId: String(c.centerId),
        totalCapacity: 1,
      }))
    : clubs;

  const getPacificDate = (date, startTime) =>
    momentTZ
      .tz(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm:ss', 'UTC')
      .tz('America/Los_Angeles')
      .format('YYYY-MM-DD');

  const displayClasses = useNewApi
    ? openClasses.filter(
        c =>
          getPacificDate(c.date, c.startTime) === targetDate.date &&
          (selectedClub === 'all' || String(c.centerId) === selectedClub)
      )
    : classes.filter(c => c.date === targetDate.date);

  return (
    <>
      <div className={styles.headerContainer}>
        <div>
          <img src={horizontalLogo} alt="logo" className={styles.logoImage} />
        </div>
      </div>
      {isRequesting && loadClubsIsRequesting && !displayClubs.length ? (
        <Spinner />
      ) : (
        <div>
          <ClassesHeader
            selectedClub={selectedClub}
            setSelectedClub={setSelectedClub}
            clubs={displayClubs}
            changeSelectedClub={changeSelectedClub}
          />
          <ClassesContent
            datesRange={datesRange}
            targetDate={targetDate}
            setTargetDate={setTargetDate}
            isMobile={isMobile}
            isRequesting={isRequesting}
            classes={displayClasses}
          />
        </div>
      )}
    </>
  );
};

GuestClasses.propTypes = {
  classes: PropTypes.array,
  clubs: PropTypes.array,
  openClasses: PropTypes.array,
  openCenters: PropTypes.array,
  loadClubs: PropTypes.func.isRequired,
  loadClassesSchedule: PropTypes.func.isRequired,
  loadOpenClasses: PropTypes.func.isRequired,
  loadOpenCenters: PropTypes.func.isRequired,
  isRequesting: PropTypes.bool.isRequired,
  loadClubsIsRequesting: PropTypes.bool.isRequired,
  changeSelectedClub: PropTypes.func.isRequired,
};

GuestClasses.defaultProps = {
  classes: [],
  clubs: [],
  openClasses: [],
  openCenters: [],
};

export default GuestClasses;
